import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, CheckCircle, Clock, XCircle, ExternalLink, RefreshCw } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate, useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransactionStatusPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { txHash } = useParams();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (txHash) {
      checkTransactionStatus();
      // Auto-refresh every 10 seconds if pending
      const interval = setInterval(() => {
        if (transaction?.status === 'pending') {
          checkTransactionStatus();
        }
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [txHash, transaction?.status]);

  const checkTransactionStatus = async () => {
    if (!checking) {
      setChecking(true);
    }
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.get(
        `${API}/crypto-tx/status/${txHash}?network=polygon`,
        config
      );
      
      setTransaction(res.data);
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error('Failed to check transaction status');
    } finally {
      setLoading(false);
      setChecking(false);
    }
  };

  const getStatusIcon = () => {
    if (!transaction) return null;
    switch (transaction.status) {
      case 'confirmed':
        return <CheckCircle className="w-16 h-16 text-green-600" />;
      case 'pending':
        return <Clock className="w-16 h-16 text-yellow-600 animate-pulse" />;
      case 'failed':
        return <XCircle className="w-16 h-16 text-red-600" />;
      default:
        return <Clock className="w-16 h-16 text-gray-600" />;
    }
  };

  const getStatusColor = () => {
    if (!transaction) return 'bg-gray-100 text-gray-800';
    switch (transaction.status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusMessage = () => {
    if (!transaction) return '';
    switch (transaction.status) {
      case 'confirmed':
        return 'Your transaction has been confirmed on the blockchain!';
      case 'pending':
        return 'Your transaction is being processed. This usually takes 1-2 minutes.';
      case 'failed':
        return 'Your transaction failed. Please try again.';
      default:
        return 'Checking transaction status...';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/crypto/wallet')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Wallet
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Transaction Status</CardTitle>
            <CardDescription>Track your crypto transaction on the blockchain</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Icon */}
            <div className="flex flex-col items-center justify-center py-6">
              {getStatusIcon()}
              <Badge className={`mt-4 text-lg px-4 py-1 ${getStatusColor()}`}>
                {transaction?.status?.toUpperCase() || 'UNKNOWN'}
              </Badge>
              <p className="text-center text-gray-600 mt-3 max-w-md">
                {getStatusMessage()}
              </p>
            </div>

            {/* Transaction Details */}
            {transaction && (
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Transaction Hash</p>
                    <p className="text-sm font-mono text-gray-900 break-all">
                      {txHash.slice(0, 10)}...{txHash.slice(-8)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Network</p>
                    <Badge variant="outline">Polygon</Badge>
                  </div>
                </div>

                {transaction.block_number && (
                  <div>
                    <p className="text-sm text-gray-600">Block Number</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {transaction.block_number}
                    </p>
                  </div>
                )}

                {transaction.gas_used && (
                  <div>
                    <p className="text-sm text-gray-600">Gas Used</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {transaction.gas_used}
                    </p>
                  </div>
                )}

                {transaction.from && (
                  <div>
                    <p className="text-sm text-gray-600">From</p>
                    <p className="text-xs font-mono text-gray-700">
                      {transaction.from}
                    </p>
                  </div>
                )}

                {transaction.to && (
                  <div>
                    <p className="text-sm text-gray-600">To</p>
                    <p className="text-xs font-mono text-gray-700">
                      {transaction.to}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3 pt-4 border-t">
              {transaction?.status === 'pending' && (
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>
                    Transaction is pending. Auto-refreshing every 10 seconds...
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={checkTransactionStatus}
                  disabled={checking}
                  variant="outline"
                  className="flex-1"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
                  Refresh Status
                </Button>
                
                {transaction?.explorer_url && (
                  <Button
                    onClick={() => window.open(transaction.explorer_url, '_blank')}
                    className="flex-1"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View on Explorer
                  </Button>
                )}
              </div>

              {transaction?.status === 'confirmed' && (
                <Button
                  onClick={() => navigate('/crypto/wallet')}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  Back to Wallet
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* What's Next */}
        {transaction?.status === 'confirmed' && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">What's Next?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-gray-600">
              <p>✅ Your transaction is complete!</p>
              <p>• The recipient's balance will be updated</p>
              <p>• You can view this transaction in your history</p>
              <p>• The transaction is permanently recorded on the Polygon blockchain</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TransactionStatusPage;
